import { useCallback, useMemo, useState } from "react";
import { ActionCard } from "./ActionCard";
import { ConfidenceSlider } from "@/components/ConfidenceSlider";
import { useModel } from "@/contexts/ModelContext";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useReviewGridView } from "@/contexts/ReviewGridViewContext";
import { BuildPromptSteps } from "@/types/flows";
import { useCurrentMedia } from "@/store";
import { useMediaUtils } from "@/hooks/useMediaUtils";
import { twClassNames } from "@scripts/react/helpers/twClassNames";

interface AdjustPredictionsActionCardProps {
    disabled?: boolean;
}

export const AdjustPredictionsActionCard = ({ disabled }: AdjustPredictionsActionCardProps) => {
    const { setPromptLabelsGlobalConfidenceThreshold } = useModel();
    const { currentFramePredictions } = useComputedValues();
    const currentMedia = useCurrentMedia();
    const { updateMediaItem } = useMediaUtils();
    const { isGridView } = useReviewGridView();

    const [confidenceThreshold, setConfidenceThreshold] = useState(
        currentMedia?.file.confidenceThreshold!
    );

    const updateConfidenceThreshold = useCallback(
        (newThreshold: number) => {
            if (!currentMedia?.file) return;

            setConfidenceThreshold(newThreshold);
            updateMediaItem({ ...currentMedia.file, confidenceThreshold: newThreshold });
            setPromptLabelsGlobalConfidenceThreshold(newThreshold);
        },
        [currentMedia?.file, updateMediaItem, setPromptLabelsGlobalConfidenceThreshold]
    );

    const visiblePredictionCount = useMemo(() => {
        if (!Array.isArray(currentFramePredictions)) return 0;
        return currentFramePredictions.filter((pred) => pred.confidence >= confidenceThreshold)
            .length;
    }, [currentFramePredictions, confidenceThreshold]);

    return (
        <ActionCard
            title="Adjust Objects"
            icon="far fa-sparkle"
            disabled={disabled}
            expandedContent={
                <ConfidenceSlider
                    initialConfidenceThreshold={currentMedia?.file.confidenceThreshold!}
                    onConfidenceThresholdChanged={updateConfidenceThreshold}
                    predictionCount={visiblePredictionCount}
                    mode={BuildPromptSteps.REVIEW_PREDICTIONS}
                    showCount={!currentMedia?.file.isVideo && !isGridView}
                    className={twClassNames(disabled && "pointer-events-none")}
                />
            }
        />
    );
};
